/*
  Module for handling analytics. Requires google tag manager in global scope.

  Not using react-gtm-module due to: https://github.com/alinemorelli/react-gtm/issues/28#issuecomment-593698270
  (keep reading other comments of people using verbatim js)
*/

import { useEffect } from 'react';
import { slugs } from '../app/steps';
import { Address, App_PortalFragment, Cart_ApplicationFragment, CustomerFragment, PortalAnalytics } from '../generated.graphql';
import { formatPhoneNumber } from 'shared/utils/phone';
import shajs from 'sha.js';


// https://support.google.com/google-ads/answer/12785317/
// Developers can optionally provide multiple values (up to 3 for phone number and email and 2 for address) by using an array value rather than a string.
// If you capture more than 1 value, providing this will increase the likelihood of a match.
type ConversionValue2<T> = T | [T] | [T, T];
type ConversionValue3<T> = ConversionValue2<T> | [T, T, T];
type ConversionAddress = {
  first_name: string;
  last_name: string;
  street?: string;      // User street address. Example: '123 Main St'
  city?: string;        // User city name. Example: 'Melbourne'
  region?: string;      // User province, state or region. Example: 'Victoria' or 'VIC'
  postal_code: string;  // User postcode (5-digit version only). Example: '12345'
  country: string;      // User country code. Example: 'AU'. Use two-letter country codes as per the ISO 3166-1 alpha-2 standard.
};
type HashedConversionAddress = {
  sha256_first_name: string; // SHA256 hashed value. Example implies trimmed, lowercase.
  sha256_last_name: string;  // SHA256 hashed value. Example implies trimmed, lowercase.
  street?: string;      // User street address. Example: '123 Main St'
  city?: string;        // User city name. Example: 'Melbourne'
  region?: string;      // User province, state or region. Example: 'Victoria' or 'VIC'
  postal_code: string;  // User postcode (5-digit version only). Example: '12345'
  country: string;      // User country code. Example: 'AU'. Use two-letter country codes as per the ISO 3166-1 alpha-2 standard.
};
type ConversionData = {
  email?: ConversionValue3<string>;
  phone_number?: ConversionValue3<string>; // User phone number. Must be in E.164 format, which means it must be 11–15 digits including a plus sign (+) prefix and country code, with no dashes, parentheses or spaces.
  address?: ConversionValue2<ConversionAddress>;
};
type HashedConversionData = {
  sha256_email_address?: ConversionValue3<string>; // SHA256 hashed value. Example implies trimmed, lowercase.
  sha256_phone_number?: ConversionValue3<string>;  // // SHA256 hashed value in E.164 format, which means it must be 11–15 digits including a plus sign (+) prefix and country code, with no dashes, parentheses or spaces.
  address?: ConversionValue2<HashedConversionAddress>;
};

// update window type to include analytics globals
declare global {
  interface Window {
    dataLayer?: any;
    gtag?: (...args: any[]) => void;
    configuredTrackingId?: string;
    enhanced_conversion_data?: ConversionData;
  }
}

/**
 * Transform cart items into data structure expected by analytics v4
 */
function transformCartItems(app: Cart_ApplicationFragment) {
  return app.items?.map((item, idx) => ({
    item_id: item.conversionId,
    item_name: item.title,
    item_category: item.category,
    //list_position: idx + 1,
    quantity: item.qty,
    price: parseFloat(item.conversionValue),
  }))
}

function getApplicationTotal(app: Cart_ApplicationFragment) {
  return {
    value: app.items?.reduce((total, item) => total + parseFloat(item.conversionValue), 0),
    currency: 'AUD',
  };
}

// Returns a GA4-compatible ecommerce object
function getEcommerceDataLayer(app: Cart_ApplicationFragment) {
  return {
    transaction_id: app.id.toString(),
    tax: 0.0,
    shipping: app.pricing.totalFreight,
    shipping_tier: app.inStore !== null ? "Pickup" : "Delivery",
    ...getApplicationTotal(app),
    items: transformCartItems(app)
  }
}

function hash(data: string | string[] | undefined): string | string[] | undefined {
  if (data === undefined)
    return undefined;
  var _hash = (str: string) => shajs('sha256').update(str.trim().toLowerCase()).digest('hex');
  if (Array.isArray(data))
    return data.map(v => _hash(v));
  return _hash(data);
}

function hashConversionAddress(address: ConversionAddress | undefined): HashedConversionAddress | undefined {
  if (address === undefined)
    return undefined;
  return {
    sha256_first_name: hash(address.first_name) as string,
    sha256_last_name: hash(address.last_name) as string,
    street: address.street,
    city: address.city,
    region: address.region,
    postal_code: address.postal_code,
    country: address.country,
  };
}

function hashConversionData(data: ConversionData): HashedConversionData {
  return {
    sha256_email_address: hash(data.email) as ConversionValue3<string> | undefined,
    sha256_phone_number: hash(data.phone_number) as ConversionValue3<string> | undefined,
    address: (Array.isArray(data.address) ? data.address.map(a => hashConversionAddress(a)) : hashConversionAddress(data.address)) as ConversionValue2<HashedConversionAddress> | undefined,
  }
}

/*
 * The primary function that will drive events in GTM/GA4.
 */
function updateCheckoutProgress(app: Cart_ApplicationFragment, curStepSlug: string, nextStepSlug: string) {
  // GA4 (see specific add*Info functions)
  window.dataLayer?.push({
    event: "application_progress",
    progress: {
      fromStep: curStepSlug,
      toStep: nextStepSlug,
    },
    ecommerce: getEcommerceDataLayer(app)
  });
}

function completedCheckout(app: Cart_ApplicationFragment) {
  window.dataLayer?.push({ ecommerce: null });  // Clear the previous ecommerce object.
  window.dataLayer?.push({
    event: "purchase",
    ecommerce: getEcommerceDataLayer(app)
  });

  // Adwords "conversion" event via adwordsConversionId requires custom setup for GA4. Removed as the "purchase" event should suffice.
  // See code history for event details.
}

function pageView(location?: string, title?: string) {
  // GA3/GA4
  window.gtag?.('event', 'page_view', {
    page_location: location ?? document.location.toString(),
    page_title: title ?? document.title,
  });

  // GA4 allows "Enhanced Tracking" which can monitor the history API for SPA page view events automatically
  // Otherwise, the above page_view will suffice for manual tracking
}

function removeEmptyKeys(result: any) {
  Object.entries(result).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value = value.filter((v: any) => !!v);
    }

    if (!value || (Array.isArray(value) && value.length === 0)) {
      delete result[key];
    } else {
      result[key] = value;
    }
  });

  return Object.entries(result).length == 0 ? null : result;
}

function updateEnhancedConversionData(customer: CustomerFragment, customerAddress?: Address | null) {
  let address: ConversionAddress | null = removeEmptyKeys({
    first_name: customer.firstName,
    last_name: customer.lastName,
    street: customerAddress?.street,
    city: customerAddress?.suburb,
    region: customerAddress?.state,
    postal_code: customerAddress?.postcode,
    country: 'AU',
  });

  // if all required fields aren't present, don't use the address object
  if (address && ![address.first_name, address.last_name, address.postal_code, address.country].every(v => !!v))
    address = null;

  const hashedResult: HashedConversionData & {
    [key: string]: any;
  } = hashConversionData({
    email: customer.email,
    phone_number: formatPhoneNumber(customer.phoneMobile, true),
    address: address ?? undefined,
  });

  // docs say to remove any empty values
  removeEmptyKeys(hashedResult);

  // GA4 (to be consumed by custom javascript in tag manager)
  window.dataLayer?.push({ "conversionData": hashedResult });
}

export interface TagManagerHookArgs extends Omit<PortalAnalytics, "__typename"> {
  portal_slug?: string;
}

export function usePortalAnalytics(portal: App_PortalFragment) {
  return useAnalytics({ ...portal.analytics, portal_slug: portal.slug });
}

export function useAnalytics(analytics: TagManagerHookArgs) {
  useEffect(() => {
    // the usual tag manager setup, but always scoped to window
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      // MUST use special "arguments" object or tag manager fails to load
      window.dataLayer.push(arguments);
    }

    let script: HTMLScriptElement | null = null;
    if (analytics.trackingId && window.configuredTrackingId !== analytics.trackingId) {
      if (analytics.portal_slug) {
        window.dataLayer.push({
          portal: {
            slug: analytics.portal_slug
          }
        });
      }
      window.dataLayer.push({
        measurementId: analytics.trackingId
      });
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });
      window.configuredTrackingId = analytics.trackingId;
      script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=GTM-PQDC9ZM`;
      document.getElementsByTagName('head')[0]?.appendChild(script);
      
      // Linked domains should be configured via GA4 admin
      // https://support.google.com/analytics/answer/10071811

      // Google ads enhanced conversions can be configured in tag manager via data layer variables
      // https://support.google.com/google-ads/answer/12785317
    }

    return () => {
      if (script) {
        document.getElementsByTagName('head')[0]?.removeChild(script);
      }
    }
  }, [ analytics?.trackingId ]);

  if (analytics.sendAllEvents) {
    return {
      updateCheckoutProgress,
      pageView,
      updateEnhancedConversionData,
      completedCheckout,
    };
  }

  return {
    updateCheckoutProgress: () => {},
    pageView,
    updateEnhancedConversionData: () => {},
    completedCheckout: () => {},
  }
}
